import {PolymerElement} from '@polymer/polymer/polymer-element.js';
import '@polymer/paper-dropdown-menu/paper-dropdown-menu.js'
import '@polymer/paper-listbox/paper-listbox.js'
import '@polymer/paper-item/paper-item.js'
import {html} from '@polymer/polymer/lib/utils/html-tag.js';
import template from './ns-paper-dropdown.html';
import {FlattenedNodesObserver} from '@polymer/polymer/lib/utils/flattened-nodes-observer.js';


/**
 * <p>A replacement for the standard ns-dropdown and ns-slotted-dropdown.
 * This dropdown is slotted to allow for more detail/versatility in selections,
 * can take an array of objects and build the content in the light-dom,
 * and uses pub/sub for events.</p>
 *
 * <h2>UX/Design Docs</h2>
 * <a href="https://nuskin.invisionapp.com/d/main/#/console/13401975/279849559/inspect" target="_blank">
 *   <img class="ux-doc" src="https://nuskin.invisionapp.com/static-signed/live-embed/242207610/279849559/2/latest/xV9norSGOlEAdlEvdmzYh1kJCi7HS6d1uwMmccDlEmRXXpeuBfTCZAlEcuhvRAP51u6ZEtNt3odfkSPPGlEwmSWxsVAlE/Standard-Dropdown-2x.png" alt="Dropdown UX Documentation">
 * </a>
 *
 * <h2>Demo</h2>
 * <demo-snippet class="demoSnippet">
 *   <template>
 *     <ns-paper-dropdown>
 *      <paper-item data-value="item1">Item 1</paper-item>
 *      <paper-item data-value="item2">Item 2</paper-item>
 *      <paper-item data-value="item2">Item 3</paper-item>
 *     </ns-paper-dropdown>
 *     <br>
 *     <p>Should have a demo showing an example using the dropdown-content slot</p>
 *   </template>
 * </demo-snippet>
 *
 * <marked-element>
 *  <div slot="markdown-html">
 *  <script type="text/markdown">
 *
 *      ### Styling
 *
 *      ##### `<ns-paper-dropdown>` provides the following custom properties and mixins for styling:
 *
 *      Custom property                 | Description                                           | Default
 *      --------------------------------|-------------------------------------------------------|-------------------
 *      `--tbd`                          | We should probably have a mixin for stuff            | `{}`
 *
 *      ### Slots
 *
 *      ##### `<ns-paper-dropdown>` provides the following slots for content:
 *
 *      Custom property                 | Description                           | Default
 *      --------------------------------|---------------------------------------|-------------------
 *      `default`                       | Content of the paper-listbox                       | ``
 *
 *  </script>
 * </marked-element>
 *
 */
export class NsPaperDropdown extends PolymerElement {
    constructor() {
        super();
    }
    
    _attachDom(dom) {
      this.attachShadow({mode: 'open', delegatesFocus: true});
      super._attachDom(dom);
      // this.appendChild(dom);
    }

    static get is() {
        return "ns-paper-dropdown";
    }

    static get template() {
        let htmlElement = document.createElement('TEMPLATE');
        htmlElement.innerHTML = template;
        return htmlElement;
    }

    static get properties() {
        return {

            /**
             * The attribute to use on menu items to look up the item title. Typing the
             * first letter of an item when the menu is open focuses that item. If unset, textContent will be used.
             * Passed on to the internal Listbox.
             */
            attrForItemTitle: String,

            /**
             * If you want to use an attribute value or property of an element for
             * `selected` instead of the index, set this to the name of the attribute
             * or property. Hyphenated values are converted to camel case when used to
             * look up the property of a selectable element. Camel cased values are
             * *not* converted to hyphenated values for attribute lookup. It's
             * recommended that you provide the hyphenated form of the name so that
             * selection works in both cases. (Use `attr-or-property-name` instead of
             * `attrOrPropertyName`.)
             * Passed on to the internal Listbox.
             */
            attrForSelected: {type: String, value: null},

            /**
             * By default, the dropdown will constrain scrolling on the page
             * to itself when opened.
             * Set to true in order to prevent scroll from being constrained
             * to the dropdown when it opens.
             */
            allowOutsideScroll: {type: Boolean, value: false},

            /**
             * Set to true to always float the label. Bind this to the
             * `<paper-input-container>`'s `alwaysFloatLabel` property.
             */
            alwaysFloatLabel: {type: Boolean, value: true},

            /**
             * If true, the user cannot interact with this element.
             */
            disabled: {type: Boolean, value: false},

            /**
             * If true, the `horizontalAlign` and `verticalAlign` properties will
             * be considered preferences instead of strict requirements when
             * positioning the dropdown and may be changed if doing so reduces
             * the area of the dropdown falling outside of `fitInto`.
             */
            dynamicAlign: {type: Boolean},

            /**
             * The error message to display when invalid.
             */
            errorMessage: {type: String, value: 'Please select an item', reflectToAttribute: true},

            /**
             * The orientation against which to align the menu dropdown
             * horizontally relative to the dropdown trigger.
             */
            horizontalAlign: {type: String, value: 'left'},

            /**
             * True if the last call to validate is invalid.
             */
            invalid: {type: Boolean, value: false, reflectToAttribute: true},

            /**
             * If true the window is desktop size.
             */
            isDesktop: {type: Boolean, value: false, observer: '_setSizing'},

            /**
             * The label for the dropdown.
             */
            label: {type: String, reflectToAttribute: true},

            /**
             * Set to true to disable animations when opening and closing the
             * dropdown.
             */
            noAnimations: {type: Boolean, value: false},

            /**
             * Set to true to disable the floating label. Bind this to the
             * `<paper-input-container>`'s `noLabelFloat` property.
             */
            noLabelFloat: {type: Boolean, value: false, reflectToAttribute: true},

            /**
             * True if the dropdown is open. Otherwise, false.
             */
            opened: {type: Boolean, notify: true, value: false, observer: '_openedChanged'},

            /**
             * The placeholder for the dropdown.
             */
            placeholder: {type: String, reflectToAttribute: true},

            /**
             * Whether focus should be restored to the dropdown when the menu closes.
             */
            restoreFocusOnClose: {type: Boolean, value: true},

            /**
             * Set to true to mark the input as required. If used in a form, a custom
             * element that uses this behavior should also use IronValidatableBehavior
             * and define a custom validation method. Otherwise, a required element will
             * always be considered valid. It's also strongly recommended to provide a
             * visual style for the element when its value is invalid.
             */
            required: {type: Boolean, value: false},

            /**
             * Gets or sets the selected element. The default is to use the index of the
             * item. Connected to the internal listbox.
             * @type {string|number}
             */
            selected: {type: String, notify: true, observer: '_selectedChanged'},

            /**
             * The derived "label" of the currently selected item. This value
             * is the `label` property on the selected item if set, or else the
             * trimmed text content of the selected item.
             */
            selectedItemLabel: {type: String, notify: true, readOnly: true},

            /**
             * The last selected item. An item is selected if the dropdown menu has
             * a child with slot `dropdown-content`, and that child triggers an
             * `iron-select` event with the selected `item` in the `detail`.
             *
             * @type {?Object}
             */
            selectedItem: {type: Object, notify: true, readOnly: true},

            /**
             * The value for this element that will be used when submitting in
             * a form. It reflects the value of `selectedItemLabel`. If set directly,
             * it will not update the `selectedItemLabel` value.
             */
            value: {type: String, notify: true},

            /**
             * The orientation against which to align the menu dropdown
             * vertically relative to the dropdown trigger.
             */
            verticalAlign: {type: String, value: 'top'},

            /**
             * Overrides the vertical offset computed in
             * _computeMenuVerticalOffset.
             */
            verticalOffset: {type: Number, value: 89},

            /**
             * Contains an array of children from the lightdom
             */
            _distNodeList: {
                type: Array, value: () => {
                    return []
                }
            },

            dirty: {
                type: Boolean,
                value: false,
                notify: true,
                reflectToAttribute: true
            },

            // =============== Properties for backward compatibility with the old ns-dropdown ===============

            optionsObjectArray: {type: Array, observer: "_setOptionsArray"}
        };
    }

    ready() {
        super.ready();
        let ironDropdown = this.$.internalDropdown.shadowRoot.querySelector('paper-menu-button').shadowRoot.querySelector('iron-dropdown');
        ironDropdown.animationConfig = {
            name: 'slide-down-animation',
            node: ironDropdown,
        };
        window.addEventListener('resize', (e) => this._handleWindowResize(e))
        this.addEventListener('resize', (e) => this._handleWindowResize(e))

        // Map out paper-dropdown-menu's events to the host
        this.$.internalDropdown.addEventListener('active-changed', (e) => {
            this.dispatchEvent(new CustomEvent('active-changed', {bubbles: false, composed: false}));
        });
        this.$.internalDropdown.addEventListener('disabled-changed', (e) => {
            this.dispatchEvent(new CustomEvent('disabled-changed', {bubbles: false, composed: false}));
        });
        this.$.internalDropdown.addEventListener('focused-changed', (e) => {
            this.dispatchEvent(new CustomEvent('focused-changed', {bubbles: false, composed: false}));
        });
        this.$.internalDropdown.addEventListener('invalid-changed', (e) => {
            this.dispatchEvent(new CustomEvent('invalid-changed', {bubbles: false, composed: false}));
        });
        this.$.internalDropdown.addEventListener('opened-changed', (e) => {
            this.dispatchEvent(new CustomEvent('opened-changed', {bubbles: false, composed: false}));
        });
        this.$.internalDropdown.addEventListener('selected-item-changed', (e) => {
            this.dispatchEvent(new CustomEvent('selected-item-changed', {bubbles: false, composed: false}));
            this.validate();
        });
        this.$.internalDropdown.addEventListener('selected-item-label-changed', (e) => {
            this.dispatchEvent(new CustomEvent('selected-item-label-changed', {bubbles: false, composed: false}));
            this.validate();
        });
        this.$.internalDropdown.addEventListener('value-changed', (e) => {
            this.dispatchEvent(new CustomEvent('value-changed', {bubbles: false, composed: false}));
        });
    }

    connectedCallback() {
        super.connectedCallback()

        // Grab the distributed children from slots and filter for only dom nodes
        this._distNodesObserver = new FlattenedNodesObserver(this, (info) => {
            this._processNewNodes(info.addedNodes.filter((node) => {
                return (node.nodeType === Node.ELEMENT_NODE)
            }))
        });

        // Replace dropdown arrow with chevron.
        this.$.internalDropdown.shadowRoot.querySelector('#menuButton > div > paper-input > iron-icon').setAttribute('icon', 'nuskin-24:icon-narrow-angle-bottom');
    }

    /**
     * Store the filtered list of dom nodes in a property for later use
     * @param {dom nodelist} nodeList
     */
    _processNewNodes(nodeList) {
        for (const key in nodeList) {
            if (nodeList.hasOwnProperty(key)) {
                const node = nodeList[key];
                this._distNodeList.push(node);
            }
        }
        if (this._distNodeList.length > 1) {
            // let listbox = this._distNodeList[0];
            let listbox = this.$.internalListbox;
            let selected = listbox.selected;
            listbox.selected = null;
            listbox.selected = selected;
            // TODO: the last item on the list isn't getting the border removed because the distributed nodes are different
            let paperItems = listbox.children;
            if (paperItems.length > 0) {
                paperItems[paperItems.length - 1].style.borderBottom = 'none';
            }
            // if (this._distNodeList.length > 1) this._distNodeList[this._distNodeList.length-1].style.borderBottom = 'none';
        }
    }

    sizeDropdownContentContainer() {
        let width = this.$.internalDropdown.offsetWidth > 0 ? this.$.internalDropdown.offsetWidth : '320';
        this.$.internalDropdown.shadowRoot.querySelector('paper-menu-button').shadowRoot.querySelector('div.dropdown-content').style.width = width + 'px';
    }

    validate() {
        return this.$.internalDropdown.validate();
    }

    clear() {
        this.invalid = false;
        this.dirty = false;
        this.dropDownOpened = false;
        this.origSelected = undefined;
    }

    _setSizing() {
        this.isDesktop ? this.verticalOffset = 59 : this.verticalOffset = 74;
    }

    _handleWindowResize(e) {
        let resizeTimer;
        clearTimeout(resizeTimer);
        resizeTimer = setTimeout(() => {
            this.sizeDropdownContentContainer();
        }, 50);
    }

    _selectedChanged(_selected) {
        if (!this.dropDownOpened) {
            this.origSelected = _selected;
        } else {
            this.dirty = this.origSelected !== _selected;
        }
    }

    _openedChanged(e) {
        this.dropDownOpened = this.dropDownOpened || e;
        this.sizeDropdownContentContainer();
    }

    // =============== Used for backward compatibility with the old ns-dropdown ===============
    _setOptionsArray() {
        if (this.optionsObjectArray) {
            // let listbox = document.createElement('PAPER-LISTBOX');
            // listbox.slot = 'dropdown-content';
            for (const option in this.optionsObjectArray) {
                if (this.optionsObjectArray.hasOwnProperty(option)) {
                    const opt = this.optionsObjectArray[option];
                    let item = document.createElement('PAPER-ITEM');
                    item.id = opt.key;
                    item.textContent = opt.value;
                    this.appendChild(item);
                }
            }
            // if(this.selected) listbox.selected = this.selected;
            // this.appendChild(listbox);
        }
    }

}

customElements.define(NsPaperDropdown.is, NsPaperDropdown);
